import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "app";
import CustomModalLayout from "../../../web/Auth/CustomModalLayout";
import { Lock } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../../shadcn/components/ui/input-otp";
import { Button } from "../../../../shadcn/components/ui/button";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { useSelector, useDispatch } from "react-redux";
import { sendOtpEmail, sendOtpMobile } from "services/bidder";
import {
  emailLoginWithOtp,
  mobileLoginWithOtp,
  getUser,
  getBidderOnboardingOfUser,
} from "services";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import {
  setBidderTokens,
  setBidderUser,
  setOnboarding,
  setBidderOnboarding,
  setUser,
  clearAuth,
  clearBidderAuth,
  clearGuestUser,
} from "store";

export default function BidderVerifyAuth() {
  const form = useBetaForm({
    user_identity: "",
    token: "",
  });
  const form_constraints = useMemo(
    () => ({
      user_identity: {
        presence: {
          allowEmpty: false,
        },
      },
      token: {
        presence: {
          allowEmpty: false,
          message: "^OTP can't be blank",
        },
      },
    }),
    []
  );

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form, form_constraints]);

  const user = useSelector((state: any) => state.bidderAuth.user);
  const accessToken = useSelector((state: any) => state.bidderAuth.accessToken);
  const email = user?.attributes?.find((a: any) => a.type === "email")?.value;
  const mobile = user?.attributes?.find(
    (a: any) => a.type === "mobile_number"
  )?.value;
  const hasEmail = Boolean(email);
  const hasMobile = Boolean(mobile);
  const hasAccessToken = Boolean(accessToken);

  const maskEmail = (email: string) => {
    if (!email) return "";
    const parts = email.split("@");
    const local = parts[0];
    const domain = parts[1] || "";
    return local.slice(0, 1) + `*****${local.slice(-1)}@` + domain;
  };
  const maskMobile = (num: string) => {
    if (!num) return "";
    const start = num.slice(0, 3);
    const end = num.slice(-1);
    return `${start}********${end}`;
  };

  const maskedEmail = useMemo(
    () => (hasEmail ? maskEmail(email as string) : ""),
    [hasEmail, email]
  );
  const maskedMobile = useMemo(
    () => (hasMobile ? maskMobile(mobile as string) : ""),
    [hasMobile, mobile]
  );

  const [phase, setPhase] = useState<"checking" | "redirecting" | "ready">(
    "checking"
  );
  const [timer, setTimer] = useState(30);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [verifyVia, setVerifyVia] = useState<any>(null);
  const [showLogoutMsg, setShowLogoutMsg] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const otpSentRef = useRef(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    return () => {
      isSubmittingRef.current = false;
    };
  }, []);
  const maskedIdentity = useMemo(() => {
    if (verifyVia === "email" && email) return maskEmail(email);
    if (verifyVia === "sms" && mobile) return maskMobile(mobile);
    return "";
  }, [verifyVia, email, mobile]);
  const dispatch = useDispatch();
  const router = useRouter();
  useEffect(() => {
    return () => {
      otpSentRef.current = false;
    };
  }, []);

  const init = useCallback(async (isResend: boolean = false) => {
    if (!isResend && otpSentRef.current) {
      console.log("Initial OTP already sent, skipping duplicate request");
      return;
    }

    if (!isResend) {
      otpSentRef.current = true;
    }

    setIsLoading(true);

    try {
      if (hasEmail) {
        await sendOtpEmail({ value: email });
        setVerifyVia("email");
      } else if (hasMobile) {
        await sendOtpMobile({ value: mobile });
        setVerifyVia("sms");
      }
    } catch (error) {
      if (!isResend) {
        otpSentRef.current = false;
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [hasEmail, email, hasMobile, mobile]);

  useEffect(() => {
    (async () => {
      // First check if user has access token
      if (!hasAccessToken) {
        setPhase("redirecting");
        const redirectUri =
          (router.query.redirect_uri as string) ||
          (typeof window !== "undefined"
            ? localStorage.getItem("bidder_redirect_uri")
            : null) ||
          "/";
        if (redirectUri) {
          router.replace(
            `/login?redirect_uri=${encodeURIComponent(redirectUri)}`
          );
        } else {
          router.replace("/login");
        }
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        const res = await fetch("/api/2fa", { method: "GET" });
        if (!res.ok) {
          setPhase("ready");
          return;
        }
        const data = await res.json();
        const today = DateTime.now()
          .setZone("Asia/Kolkata")
          .toFormat("yyyy-MM-dd");
        if (data?.date && data.date === today) {
          setPhase("redirecting");
          const redirectUri =
            (router.query.redirect_uri as string) ||
            (typeof window !== "undefined"
              ? localStorage.getItem("bidder_redirect_uri")
              : null) ||
            "/";
          if (redirectUri) {
            router.replace(redirectUri);
          } else {
            router.replace("/");
          }
        } else {
          setPhase("ready");
        }
      } catch (e) {
        setPhase("ready");
      }
    })();
  }, [router, hasAccessToken]);

  const getISTDate = () => {
    return DateTime.now().setZone("Asia/Kolkata").toFormat("yyyy-MM-dd");
  };

  useEffect(() => {
    if (phase !== "ready") return;
    if (!hasAccessToken) return;
    if (!verifyVia && (hasEmail || hasMobile)) {
      if (otpSentRef.current) {
        console.log("OTP already sent in effect, skipping");
        return;
      }
      init();
    }
  }, [init, verifyVia, hasEmail, hasMobile, phase, hasAccessToken]);

  useEffect(() => {
    const nextIdentity =
      verifyVia === "email" ? email : verifyVia === "sms" ? mobile : "";
    const currentIdentity = form.getField("user_identity");
    if (nextIdentity !== currentIdentity) {
      form.setField("user_identity", nextIdentity);
    }
    if (form.getField("token")) {
      form.setField("token", "");
      form.removeError("token");
    }
    setErrorMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyVia, email, mobile]);

  let guestUser = useSelector((state: any) => {
    return state.auth.guestUser;
  });
  const handleResendOtp = async () => {
    if (timer > 0) return;
    try {
      setErrorMessage("");
      await init(true);
      setTimer(30);
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      setErrorMessage("Failed to resend OTP. Please try again.");
    }
  };
  const handleAfterLogin = async (tokenResponse: any) => {
    try {
      dispatch(setBidderTokens({ access_token: tokenResponse?.access_token }));
      await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken: tokenResponse }),
      });
      const userData: any = await getUser();
      dispatch(setBidderUser(userData));
      dispatch(setUser(userData));
      if (userData?.uuid) {
        const onboardingData: any = await getBidderOnboardingOfUser(
          userData?.uuid
        );
        dispatch(setBidderOnboarding(onboardingData));
        dispatch(setOnboarding(onboardingData));
      }
      try {
        await fetch("/api/2fa", { method: "POST" });
      } catch (e) {
        console.error("Failed to mark 2FA verified for today", e);
      }
      const redirectUri =
        (router.query.redirect_uri as string) ||
        (typeof window !== "undefined"
          ? localStorage.getItem("bidder_redirect_uri")
          : null) ||
        "/";
      if (redirectUri) {
        router.replace(redirectUri);
      } else {
        router.replace("/");
      }
    } catch (err) {
      console.error("Post-OTP login flow failed", err);
    }
  };

  const logoutAndRedirect = async () => {
    try {
      await fetch("/api/logout", { method: "GET" });
    } catch (e) {}
    router.replace("/login");
    dispatch(clearBidderAuth());
    dispatch(clearAuth());
    dispatch(clearGuestUser());
    localStorage.removeItem("2faVerifiedDate");
    localStorage.removeItem("tabIds");
    sessionStorage.removeItem("tabId");
    sessionStorage.removeItem("refreshing");
    sessionStorage.removeItem("activeTabId");
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setErrorMessage("");
    const otp: string = form.getField("token");
    if (!otp || otp.length < 6) {
      form.errors.set({ token: ["Please enter 6-digit OTP"] });
      return;
    }
    if (!form.validate()) return;
    setIsLoading(true);
    try {
      if (verifyVia === "email") {
        const res: any = await emailLoginWithOtp(form);
        await handleAfterLogin(res);
      } else if (verifyVia === "sms") {
        const currentIdentity: string = form.getField("user_identity") || "";
        const sanitizedIdentity = currentIdentity.replace(/^\+91/, "");
        const needsSanitization = sanitizedIdentity !== currentIdentity;
        try {
          if (needsSanitization) {
            form.setField("user_identity", sanitizedIdentity);
          }
          const res: any = await mobileLoginWithOtp(form);
          await handleAfterLogin(res);
        } finally {
          if (needsSanitization) {
            form.setField("user_identity", currentIdentity);
          }
        }
      } else {
        setErrorMessage("Unsupported verification channel.");
      }
    } catch (error: any) {
      console.error("OTP verification failed:", error);
      setErrorMessage(error?.message || "Incorrect OTP. Please try again.");
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let countdown: NodeJS.Timeout | null = null;

    if (timer > 0) {
      setIsButtonDisabled(true);
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsButtonDisabled(false);
    }

    return () => {
      if (countdown) clearInterval(countdown);
    };
  }, [timer]);
  return (
    <>
      {phase !== "ready" ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader height="h-16" width="w-16" color="text-brand-orange" />
        </div>
      ) : (
        <CustomModalLayout
          open={true}
          showCross={false}
          childrenClassName="px-4 lg:px-16 py-3 lg:py-6 bg-brand-bgGrey"
          className="bg-brand-bgGrey pt-0 w-full mx-4 max-w-[550px] rounded-2xl overflow-y-scroll max-h-[calc(100%-10px)]"
        >
          <div className="flex flex-col font-openSans relative">
            {showLogoutMsg && (
              <div className="absolute top-0 bg-red-100 border border-red-400 text-red-700 px-2 py-1 rounded shadow-lg z-50 w-full flex items-center text-xs font-openSans">
                <span>
                  Your session has expired. Please authenticate your account
                  again.
                </span>
                <button
                  className="ml-4 text-red-700 font-bold"
                  onClick={() => setShowLogoutMsg(false)}
                >
                  ×
                </button>
              </div>
            )}
            <div className="text-center">
              <div className="inline-block p-3 rounded-full bg-[#efdacf]">
                <Lock className="w-7 h-7 text-brand-orange" />
              </div>
              <h2 className="mt-2 text-xl font-semibold text-brand-textBlack font-openSans">
                Verify Authentication
              </h2>
            </div>
            <div className="mt-4">
              <div className="flex space-x-2items-center">
                <p className="font-openSans font-normal text-sm leading-[22px] text-brand-textGrey">
                  OTP sent to
                  {hasEmail ? (
                    <strong className="font-semibold"> {maskedEmail} </strong>
                  ) : null}
                  {hasEmail && hasMobile ? " and " : null}
                  {hasMobile ? (
                    <strong className="font-semibold"> {maskedMobile} </strong>
                  ) : null}
                  {" ."}
                </p>
              </div>
              <p className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack mt-8">
                Verify OTP
              </p>
              <div className="w-full flex  mt-2">
                <InputOTP
                  value={form.getField("token")}
                  onChange={(v: any) => form.setField("token", v)}
                  maxLength={6}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                >
                  <InputOTPGroup className=" w-full gap-x-3">
                    <InputOTPSlot
                      className="bg-white border-none rounded-none h-[58px] w-10 sm:w-12"
                      index={0}
                    />
                    <InputOTPSlot
                      className="bg-white border-none rounded-none  h-[58px] w-10 sm:w-12"
                      index={1}
                    />
                    <InputOTPSlot
                      className="bg-white border-none rounded-none h-[58px] w-10 sm:w-12"
                      index={2}
                    />
                    <InputOTPSlot
                      className="bg-white border-none rounded-none h-[58px] w-10 sm:w-12"
                      index={3}
                    />
                    <InputOTPSlot
                      className="bg-white border-none rounded-none h-[58px] w-10 sm:w-12"
                      index={4}
                    />
                    <InputOTPSlot
                      className="bg-white border-none rounded-none h-[58px] w-10 sm:w-12"
                      index={5}
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {form.errors.get("token") ? (
                <p className="mt-2 text-sm text-red-500">
                  {form.errors.get("token")[0]}
                </p>
              ) : null}

              <div className="mt-7 font-openSans font-normal text-web-body-sm leading-[18px] text-[#717272] w-full">
                <div className="flex space-x-2 justify-end">
                  <p className="">Didn’t get OTP? </p>
                  {timer > 0 ? (
                    <p>Resend OTP in {timer} sec</p>
                  ) : (
                    <button
                      className="underline"
                      onClick={handleResendOtp}
                      disabled={isButtonDisabled || isLoading}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Button
            className="w-full my-4"
            disabled={isLoading || (form.getField("token") || "").length < 6}
            onClick={async () => await handleSubmit()}
          >
            {isLoading ? "Verifying..." : "Verify"}
          </Button>
          <div className="flex justify-center mt-3">
            <button
              type="button"
              onClick={logoutAndRedirect}
              className="flex items-center gap-2 text-xs text-brand-orange hover:underline cursor-pointer"
            >
              Switch to a different account
            </button>
          </div>
        </CustomModalLayout>
      )}
    </>
  );
}
